import { Container, TextField } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { IBusinessUnitAPI } from '../../fetch/line-of-business';
import { useToastContext } from '../../hooks';
import { IBusinessUnit } from '../../models/line-of-business';

export const LineOfBusiness: FC = () => {
  // Non-state
  const { showToast } = useToastContext();

  // Local state
  const [newElementIndex, setNewElementIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  // If not false, this is the row the user seeks to delete
  const [isDeletingBusinessUnit, setIsDeletingBusinessUnit] = useState<false | IBusinessUnit>(
    false
  );

  // Local state from the network
  const [rows, setRows] = useState<IBusinessUnit[]>([]);

  const removeRow = (row: IBusinessUnit): void => {
    setRows((state: IBusinessUnit[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Business Unit Name',
        verticalAlignment: 'top',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBusinessUnit; index: number } };
        }) => {
          const [businessUnitName, setBusinessUnitName] = useState(original.name);

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  className: 'business-unit-name-input',
                }}
                autoFocus={index === newElementIndex}
                placeholder="Business Unit Name"
                value={businessUnitName}
                onChange={event => setBusinessUnitName(event.target.value)}
                onBlur={() => {
                  // If user clicked away and changes have not been saved, enforce the viewport does not move.
                  if (hasChanges === false) {
                    Promise.resolve().then(() => {
                      const node = document.getElementById(
                        `bank-account-wiring-instructions-${index}`
                      );
                      node?.focus?.();
                      node?.scrollIntoView?.();
                    });
                  }

                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].name !== businessUnitName) {
                      setHasChanges(true);
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].name = businessUnitName;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netSuiteDepartmentId',
        Header: 'NetSuite Department Id',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBusinessUnit; index: number } };
        }) => {
          const [netSuiteDepartmentId, setNetSuiteDepartmentId] = useState<number | string>(
            original.netSuiteDepartmentId ?? ''
          );
          return (
            <Box width="100%">
              <TextField
                fullWidth
                inputProps={{
                  id: `business-unit-net-suite-id-${index}`,
                }}
                minRows={2}
                maxRows={10}
                placeholder="NetSuite Department Id"
                value={netSuiteDepartmentId}
                onChange={e => {
                  const re = /^-?[0-9\b]+$/;
                  // make sure it is number only before we update state
                  const isNumber = re.test(e.target.value);
                  const isEmptyString = e.target.value === '';
                  if (isNumber) {
                    setNetSuiteDepartmentId(+e.target.value);
                  }
                  if (isEmptyString) {
                    setNetSuiteDepartmentId('');
                  }
                }}
                // Once the user leaves the input, take the changes they've written and commit
                // them to local state. This readies these changes for the ultimate batch save action.
                onBlur={() => {
                  // If user clicked away and changes have not been saved, enforce the viewport does not move.
                  if (hasChanges === false) {
                    Promise.resolve().then(() => {
                      const node = document.getElementById(`business-unit-net-suite-id-${index}`);
                      node?.scrollIntoView?.();
                      node?.blur?.();
                    });
                  }

                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].netSuiteDepartmentId !== netSuiteDepartmentId) {
                      setHasChanges(true);
                      // state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].netSuiteDepartmentId =
                      typeof netSuiteDepartmentId === 'string' ? null : netSuiteDepartmentId;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netSuiteARAccountId',
        Header: 'NetSuite A/R Account ID',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBusinessUnit; index: number } };
        }) => {
          const [netSuiteARAccountId, setNetSuiteARAccountId] = useState<number | string>(
            original.netSuiteARAccountId ?? ''
          );
          return (
            <Box width="100%">
              <TextField
                fullWidth
                inputProps={{
                  id: `business-unit-net-suite-ar-account-id-${index}`,
                }}
                placeholder="NetSuite A/R Account ID"
                value={netSuiteARAccountId}
                onChange={e => {
                  const re = /^-?[0-9\b]+$/;
                  // make sure it is number only before we update state
                  const isNumber = re.test(e.target.value);
                  const isEmptyString = e.target.value === '';
                  if (isNumber) {
                    setNetSuiteARAccountId(+e.target.value);
                  }
                  if (isEmptyString) {
                    setNetSuiteARAccountId('');
                  }
                }}
                // Once the user leaves the input, take the changes they've written and commit
                // them to local state. This readies these changes for the ultimate batch save action.
                onBlur={() => {
                  // If user clicked away and changes have not been saved, enforce the viewport does not move.
                  if (hasChanges === false) {
                    Promise.resolve().then(() => {
                      const node = document.getElementById(
                        `business-unit-net-suite-ar-account-id-${index}`
                      );
                      node?.scrollIntoView?.();
                      node?.blur?.();
                    });
                  }

                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].netSuiteARAccountId !== netSuiteARAccountId) {
                      setHasChanges(true);
                      // state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].netSuiteARAccountId =
                      typeof netSuiteARAccountId === 'string' ? null : netSuiteARAccountId;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netSuiteARAccountName',
        Header: 'NetSuite A/R Account Name',
        verticalAlignment: 'top',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBusinessUnit; index: number } };
        }) => {
          const [netSuiteARAccountName, setNetSuiteARAccountName] = useState(
            original.netSuiteARAccountName ?? ''
          );

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  className: 'business-unit-netsuite-AR-name-input',
                }}
                autoFocus={index === newElementIndex}
                placeholder="NetSuite A/R Account Name"
                value={netSuiteARAccountName}
                onChange={event => setNetSuiteARAccountName(event.target.value)}
                onBlur={() => {
                  // If user clicked away and changes have not been saved, enforce the viewport does not move.
                  if (hasChanges === false) {
                    Promise.resolve().then(() => {
                      const node = document.getElementById(
                        `business-unit-netsuite-AR-name-input-${index}`
                      );
                      node?.focus?.();
                      node?.scrollIntoView?.();
                    });
                  }

                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].netSuiteARAccountName !== netSuiteARAccountName) {
                      setHasChanges(true);
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].netSuiteARAccountName = netSuiteARAccountName;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
    ];
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, rows, isSaving]);

  useEffect(() => {
    fetchOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const units: IBusinessUnit[] = await IBusinessUnitAPI.getAll();
      setRows(units);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : displayFailure('get', 'business units');
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  return (
    <Page title="Business Unit">
      <Container maxWidth={false} disableGutters>
        <AdminTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          hasChanges={hasChanges}
          isSaving={isSaving}
          onAddFocusClass="business-unit-input"
          onAdd={() => {
            // Add new empty row
            setRows(state => state.concat(IBusinessUnit.getDefault()));
            setNewElementIndex(rows.length);
          }}
          onSave={async function upsertBusinessUnits() {
            setIsSaving(true);
            try {
              const newRows: IBusinessUnit[] = await IBusinessUnitAPI.upsert(rows);

              // After successful update, update UI
              setRows(newRows);

              // Reset save button to disabled
              setHasChanges(false);

              showToast('success', 'Successfully saved business unit.');
            } catch (error) {
              const message =
                error instanceof Error ? error.message : displayFailure('update', 'business units');
              showToast('error', message);
            } finally {
              setIsSaving(false);
            }
          }}
          onDelete={(original, _index) => {
            if (original.businessUnitTypeId === IBusinessUnit.getDefault().businessUnitTypeId) {
              removeRow(original);
              showToast('info', 'Removed unsaved business unit.');
            } else {
              setIsDeletingBusinessUnit(original);
            }
          }}
        />
      </Container>

      {/** ConfirmationDialogue */}
      {!!isDeletingBusinessUnit && (
        <ConfirmationDialogue
          open
          id="delete-business-unit"
          title={`Delete Business Unit ${isDeletingBusinessUnit.name}`}
          onClose={() => setIsDeletingBusinessUnit(false)}
          onConfirm={async function deleteBusinessUnit() {
            // Check if modal is open and has proper data
            if (!isDeletingBusinessUnit) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingBusinessUnit.businessUnitTypeId ===
              IBusinessUnit.getDefault().businessUnitTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingBusinessUnit);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await IBusinessUnitAPI.delete(isDeletingBusinessUnit.businessUnitTypeId);
                // Update state after only successful delete
                removeRow(isDeletingBusinessUnit);
                showToast('success', 'Successfully deleted business unit.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'business unit');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingBusinessUnit(false);
          }}
        />
      )}
    </Page>
  );
};

function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
